/* eslint-disable no-unused-expressions */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable no-nested-ternary */

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
    selectChaIsMessageProgress,
    selectCurrentUserId,
    selectIsChatFavourite,
    selectIsChatMarkUnread,
    selectIsChatMuted,
    setActiveChat,
    setActiveChunk,
    setScrollToMessageUuid,
    updateMarkChatUnread,
} from '../../../../redux/slices';
import { formatChatPreviewTime, getDisplayColor } from '../../../../helpers';
import { IChatMessage, isIntegration, isThread } from '../../../../types';
import { useTypedDispatch, useTypedSelector } from '../../../../redux/hooks';
import Icon from '../../../Icon';
import { usePolyglot } from '../../../../context/Polyglot';
import StyledDropdown, { StyledDropdownOption } from '../../../StyledComponents/StyledDropdown';
import AriaButton from '../../../AriaComponents/AriaButton';
import { useDropdownDirection } from "../../../../helpers/useDropdownDirection";
import { focusOnConversationThreadMessage, selectActiveConversation, selectConversationById, selectConversationTypers } from '../../../../redux/slices/chatNSlice';
import { ConversationIcon } from './ConversationIcon';
import { ChatMessage, Conversation, nebulaChatApi, useChatChatter } from '../../../../redux/services/nebulaChat';
import { TypingUserList } from '../../../../pages/chatN/ChatInput/TypingUsers';
import TypingLoader from '../../../loading/TypingLoader/TypingLoader';
import { ConvoMessageStatusIcon } from '../../../icons/ConvoMessageStatusIcon';
import { selectLatestMessage } from '../../../../redux/slices/messageSlice';
import { MarkdownContainer } from '../../../../pages/chatG/MarkdownComponents';
import threadStyles from '../ThreadRow/ThreadRow.module.scss';
import EntityIcon from '../../../icons/EntityIcon';
import { AnimatedDiv } from '../../../../animation';
import { useAppContext } from '../../../../context/AppContext/AppContext';
import ConversationProvider, { useConversationContext } from '../../../../context/ChatContext/contextConvo';

interface ChatRowProps {
    convo: Conversation,
    message?: IChatMessage;
    setSearchValue?: (value: string) => void;
    searchResult?: boolean;
    globalMessageSearch?: boolean;
}

interface ConversationRowProps {
    message?: IChatMessage;
    setSearchValue?: (value: string) => void;
    searchResult?: boolean;
    globalMessageSearch?: boolean;
}

interface IChatRowOptions {
    showOptions: boolean;
    setShowOptions: (val: boolean) => void;
}

const ChatRowOptions: React.FC<IChatRowOptions> = ({
    setShowOptions,
    showOptions
}) => {
    const { conversation, updateMember } = useConversationContext();

    const isMarkUnread = useTypedSelector(state => selectIsChatMarkUnread(state, conversation.uuid));

    const dispatch = useTypedDispatch();

    const [markChatRead] = nebulaChatApi.endpoints.markChatRead.useMutation();

    const { t } = usePolyglot();

    const { containerRef, dropdownUp } = useDropdownDirection(showOptions, 170);

    const handleReadToggle = () => {
        if (isMarkUnread) {
            dispatch(updateMarkChatUnread(conversation.uuid, false));
        } else if (conversation.unread > 0) {
            markChatRead({ to: conversation.uuid, to_type: conversation.type, is_read: true });
        } else {
            dispatch(updateMarkChatUnread(conversation.uuid, true));
        }
    };

    const opts = useMemo(() => {
        const options: StyledDropdownOption[] = [
            {
                label:
                    conversation.unread !== 0 || isMarkUnread
                        ? t('actions.mark_as_read')
                        : t('actions.mark_as_unread'),
                onSelect: handleReadToggle
            }
        ];

        if (!isThread(conversation)) {
            options.push({
                label: t(`actions.${conversation.is_muted ? 'unmute' : 'mute'}_notifications`),
                onSelect: () => {
                    updateMember({
                        is_muted: !conversation.is_muted
                    });
                }
            });
        }

        options.push({
            label: t(`actions.${conversation.is_favourite ? 'unpin' : 'pin'}_chat`),
            onSelect: () => {
                updateMember({
                    is_favourite: !conversation.is_favourite
                });
            }
        });

        return options;
    }, [
        conversation.is_muted,
        conversation.is_favourite,
        conversation.unread,
        conversation.unread,
        isMarkUnread
    ]);

    return (
        <div className='recent-row__options' ref={containerRef}>
            <StyledDropdown
                options={opts}
                iconOnly
                className={`contact__options-container ${dropdownUp ? 'dropdown--up' : ''}`}
                open={showOptions}
                setOpen={setShowOptions}
            />
        </div>
    );
};

interface IRowNotificationBubble {
    chatId?: string;
    unreadCount: number;
    isSearchResult?: boolean | undefined;
}

export const RowNotificationBubble: React.FC<IRowNotificationBubble> = ({
    chatId,
    unreadCount,
    isSearchResult
}) => {
    const isMarkUnread = useTypedSelector(state => selectIsChatMarkUnread(state, chatId));

    if (chatId && isMarkUnread) {
        return (
            <div className='notification-bubble'>
                <p />
            </div>
        );
    }

    if (unreadCount === 0 || (unreadCount > 0 && isSearchResult)) {
        return null;
    }

    return (
        <div className='notification-bubble'>
            <p>{unreadCount < 100 ? unreadCount : <span>99+</span>}</p>
        </div>
    );
};

export const useConversationName = (convo: Conversation): string => {
    const chatter = useChatChatter({ userUuid: convo.type === 'user' ? convo.uuid : undefined });

    if (convo.type === 'user') {
        return chatter.name;
    }

    if (convo.type === 'integration') {
        return convo.integration?.name || convo.display_name;
    }

    return convo.display_name;
}

const LastMessageFrom = memo(({ userUuid }: { userUuid: string }) => {
    const localUser = useTypedSelector(selectCurrentUserId);
    const chatter = useChatChatter({ userUuid });

    if (userUuid === localUser) {
        return <span>You:&nbsp;</span>;
    }

    return <span>{chatter.name}:&nbsp;</span>;
});

export const ConversationRow: React.FC<ChatRowProps> = ({
    convo,
    message,
    setSearchValue,
    searchResult,
    globalMessageSearch
}) => (
    <ConversationProvider key={convo.uuid} conversation={convo}>
        <DisplayConversation
            message={message}
            setSearchValue={setSearchValue}
            searchResult={searchResult}
            globalMessageSearch={globalMessageSearch}
        />
    </ConversationProvider>
);

const DisplayConversation: React.FC<ConversationRowProps> = ({
    message,
    setSearchValue,
    searchResult,
    globalMessageSearch
}) => {
    const { conversation: convo, updateMember } = useConversationContext();

    const conversationName = useConversationName(convo);

    const userUuid = useTypedSelector(selectCurrentUserId);
    const activeChatId = useTypedSelector(selectActiveConversation)?.uuid;

    const oldMuted = useTypedSelector(state => selectIsChatMuted(state, convo.uuid));
    const oldFavourite = useTypedSelector(state => selectIsChatFavourite(state, convo.uuid));

    const chatMessageInProgress = useTypedSelector(state => selectChaIsMessageProgress(state, convo.uuid));
    const typingUsers = useTypedSelector(state => selectConversationTypers(state, convo.uuid));

    const [markChatRead] = nebulaChatApi.endpoints.markChatRead.useMutation();

    const displayMessage = useTypedSelector(state => {
        if (message) {
            return message
        }

        return selectLatestMessage(state, convo.uuid, convo.last_message_uuid);
    });

    const isMuted = convo.is_muted || oldMuted;
    const isFavourite = convo.is_favourite || oldFavourite;

    const [showOptions, setShowOptions] = useState<boolean>(false);

    nebulaChatApi.endpoints.getChatMembers.useQuery({
        group_type: convo?.type || "",
        chat_uuid: convo?.uuid || ""
    }, {
        skip: !convo || convo.type === 'thread' || convo.type === 'integration'
    });

    const dispatch = useTypedDispatch();

    const { t } = usePolyglot();

    useEffect(() => {
        if (oldMuted && !convo.is_muted) {
            updateMember({
                is_muted: true
            });
        }
        if (oldFavourite && !convo.is_favourite) {
            updateMember({
                is_favourite: true
            })
        }
    }, []);

    const onRowClick = () => {
        dispatch(setScrollToMessageUuid(''))
        dispatch(setActiveChunk({ chat_uuid: convo.uuid, active_chunk: 'initial' }));
        dispatch(setActiveChat(convo.uuid));

        // if (convo.unread > 0) {
        markChatRead({
            to: convo.uuid,
            to_type: convo.type,
            is_read: true
        });
        // }
        setSearchValue && setSearchValue('');
    };

    return (
        <div
            className='chat__chat-row'
            data-is-muted={isMuted || null}
        // data-is-thread={isThread(chat) || null}
        >
            <div
                className={`recent-row${activeChatId === convo.uuid && !searchResult ? ' active' : ''}`}
                onContextMenu={e => {
                    e.preventDefault();
                    setShowOptions(!showOptions);
                }}
                onClick={onRowClick}
                onKeyDown={event => {
                    if ([' ', 'Enter'].includes(event.key)) {
                        event.preventDefault();
                        onRowClick();
                    }
                }}
                tabIndex={0}
            >
                <ConversationIcon
                    convo={convo}
                />
                <div
                    className='recent-row__content'
                    data-unread={convo.unread !== 0 || null}
                    data-is-global-search={globalMessageSearch || null}
                >
                    <div>
                        <span className='recent-row__content-icons'>
                            {isFavourite && (
                                <AriaButton
                                    onClick={() => {
                                        updateMember({
                                            is_favourite: false
                                        });
                                    }}
                                    tabIndex={-1}
                                >
                                    <Icon name='pin' width={20} height={20} />
                                </AriaButton>
                            )}
                            {isMuted ? (
                                <AriaButton
                                    onClick={() => {
                                        updateMember({
                                            is_muted: false
                                        });
                                    }}
                                    tabIndex={-1}
                                >
                                    <Icon name='muteFilled' width={20} height={20} />
                                </AriaButton>
                            ) : null}
                        </span>
                        <h3 className={convo.unread !== 0 ? 'unread' : ''}>
                            {convo.type === 'channel' && typingUsers && typingUsers?.length > 0 ? (
                                <TypingUserList users={typingUsers} />
                            ) : conversationName}
                        </h3>
                        {chatMessageInProgress && !searchResult && (
                            <i className='icon-pencil' title={t('phrases.chat_in_progress')} />
                        )}
                        <p className='recent-row__message-time'>{displayMessage ? formatChatPreviewTime(displayMessage.time) : null}</p>
                    </div>
                    <div className='recent-row__message-preview'>
                        {typingUsers && typingUsers.length > 0 ? (
                            <div className='recent-row__message-preview-typing'>
                                <TypingLoader place='sidebar' />
                            </div>
                        ) : (
                            <>
                                <p
                                    data-is-unread={(!isMuted && !!convo.unread) || null}
                                    onClick={e => {
                                        if ((e.target as HTMLElement).nodeName === 'A')
                                            e.stopPropagation();
                                    }}
                                >
                                    {displayMessage ? (
                                        <>
                                            <LastMessageFrom userUuid={displayMessage.from} />
                                            {displayMessage.deleted || displayMessage.deleted_at ? (
                                                <p style={{ fontStyle: 'italic', opacity: '0.5' }}>
                                                    {t('phrases.message_deleted')}
                                                </p>
                                            ) : (
                                                <MarkdownContainer plain>
                                                    {displayMessage.content}
                                                </MarkdownContainer>
                                            )}
                                            {!displayMessage.content &&
                                                displayMessage.files &&
                                                displayMessage.files.map(file => file.name).join(', ')}
                                        </>
                                    ) : null}
                                </p>
                                {!isIntegration(convo) && displayMessage && displayMessage.from === userUuid ? (
                                    <ConvoMessageStatusIcon
                                        time={displayMessage.time}
                                        from={displayMessage.from}
                                        conversation_uuid={convo.uuid}
                                    />
                                ) : null}
                            </>
                        )}
                    </div>
                    {!isMuted ? (
                        <RowNotificationBubble
                            chatId={convo.uuid}
                            isSearchResult={searchResult}
                            unreadCount={convo.unread}
                        />
                    ) : null}
                </div>
                {convo.uuid !== '__placeholder__' ? (
                    <ChatRowOptions
                        setShowOptions={setShowOptions}
                        showOptions={showOptions}
                    />
                ) : null}
            </div>
        </div>
    )
};

export const ConversationThreadRow: React.FC<{ thread: Conversation }> = ({
    thread,
}) => {
    const parent = useTypedSelector(state => selectConversationById(state, thread.parent_message?.to));

    if (thread.type !== 'thread' || !thread.parent_message || !parent) {
        return null;
    }

    return <ConversationThread thread={thread} parent_message={thread.parent_message} parent={parent} />
};

interface ThreadPros {
    parent: Conversation,
    thread: Conversation,
    parent_message: ChatMessage
}

const ConversationThread: React.FC<ThreadPros> = ({
    parent,
    thread,
    parent_message,
}) => {
    const { darkMode } = useAppContext();
    const [isHovered, setIsHovered] = useState(false);

    const oldMuted = useTypedSelector(state => selectIsChatMuted(state, parent.uuid));

    const threadMessage = useTypedSelector(state => selectLatestMessage(state, thread.uuid, thread.last_message_uuid));

    const [markChatRead] = nebulaChatApi.endpoints.markChatRead.useMutation();

    const originalChatter = useChatChatter({ userUuid: parent_message.from });
    const threadChatter = useChatChatter({ userUuid: threadMessage?.from });

    const dispatch = useTypedDispatch();

    const isMuted = parent.is_muted || oldMuted;

    const onThreadClick = useCallback(() => {
        markChatRead({
            to: thread.uuid,
            to_type: thread.type,
            is_read: true
        });
        dispatch(focusOnConversationThreadMessage({ parentConversationUuid: parent.uuid, messageUuid: parent_message.uuid }));
    }, [dispatch, thread, parent, parent_message, markChatRead]);

    return (
        <div
            className={threadStyles.thread_row_container}
            onMouseOver={() => setIsHovered(true)}
            onFocus={() => setIsHovered(true)}
            onMouseOut={() => setIsHovered(false)}
            onBlur={() => setIsHovered(false)}
            onClick={() => onThreadClick()}
            onKeyDown={event => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    onThreadClick();
                }
            }}
            // /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
            tabIndex={0}
            data-is-muted={isMuted || null}
            data-is-thread='true'
            data-is-unread={(!isMuted && (thread.unread || 0) > 0) || null}
        >
            <div className={threadStyles.row_header}>
                <button className={threadStyles.parent_chat_button} onClick={e => {
                    e.stopPropagation();
                    onThreadClick();
                }}>
                    {parent.type === 'user' ? 'To You' : (<>In&nbsp;<span>{parent.display_name}</span></>)}
                </button>
                {isMuted ? (
                    <div className={threadStyles.muted_icon}>
                        <Icon name='muteFilled' width={20} height={20} />
                    </div>
                ) : null}
                {!isMuted && (thread.unread || 0) > 0 ? (
                    <RowNotificationBubble unreadCount={thread.unread} />
                ) : null}
            </div>
            <div className={threadStyles.messages_container}>
                <div className={threadStyles.original_message}>
                    <EntityIcon
                        type='user'
                        details={{
                            sip_name: originalChatter.sip_name,
                            display_name: originalChatter.name,
                            avatar_key: originalChatter.file_key,
                            blf: true,
                            uuid: originalChatter.uuid,
                        }}
                    />
                    <div className={threadStyles.message_content}>
                        <div className={threadStyles.message_content_header} data-is-blank="true">
                            <h4>
                                {originalChatter.name}
                            </h4>
                            {parent_message ? (
                                <AnimatedDiv
                                    visible={isHovered}
                                    inAnimation={['fade', 'slideFromRight']}
                                    transition={{ duration: .4 }}>
                                    <p className={threadStyles.time}>
                                        {formatChatPreviewTime(parent_message.time)}
                                    </p>
                                </AnimatedDiv>
                            ) : null}
                        </div>
                        <p>
                            <MarkdownContainer plain>{parent_message.content}</MarkdownContainer>
                        </p>
                    </div>
                </div>
                <div className={threadStyles.comments_container}>
                    {threadMessage ? (
                        <div className={threadStyles.comments_list_container}>
                            <div className={threadStyles.thread_comment}>
                                <div className={threadStyles.message_content}>
                                    <div className={threadStyles.message_content_header}>
                                        {threadChatter ? (
                                            <h4 className={getDisplayColor(threadChatter.uuid, false, darkMode)}>
                                                {threadChatter.name}
                                            </h4>
                                        ) : null}
                                        {threadMessage?.time ? (
                                            <AnimatedDiv
                                                visible={isHovered}
                                                inAnimation={['fade', 'slideFromRight']}
                                                transition={{ duration: .4 }}
                                            >
                                                <p className={threadStyles.time}>
                                                    {formatChatPreviewTime(threadMessage.time)}
                                                </p>
                                            </AnimatedDiv>
                                        ) : null}
                                    </div>
                                    <p>
                                        <MarkdownContainer plain>{threadMessage.content}</MarkdownContainer>
                                        {!threadMessage.content && threadMessage.files && threadMessage.files.map(file => file.name).join(", ")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
